import { EnumButtonName } from '@calm/evexia-analytics';
import { Cancel } from '@mui/icons-material';
import { useAudio } from '@rhg/hooks/use-audio';
import playerMessages from '@rhg/lang/messages/player';
import { Box, IconButton, Slide, Typography } from '@rhg/libs/material';
import { PauseCtaFilled, PlayCtaFilled } from '@rhg/libs/material/icons';
import { styleMaxLines, sxAppend } from '@rhg/libs/material/sx';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { AudioTimeline } from '../timeline';
import {
    AudioMinimalControlsProps,
    AudioPlayerControls,
    AudioTimelineVariant,
} from '../types';

const styleAudioControlsContainer = {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
};

const styleFloatingContainer = {
    backgroundColor: 'background.paper',
};

const styleAudioControlsMain = {
    height: 'calc(100% - 5px)',
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'relative',
    pr: 4,
};

const styleAudioControlsLeft = {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    position: 'relative',
    gap: 4,
};

const styleSliderContainer = { width: '100%' };

export function AudioMinimalControls({
    onStop,
    title,
    subtitle,
    image,
    isFloating = false,
    sx,
    controlsSx,
    controls,
    areControlsHidden,
    onControlClick,
}: AudioMinimalControlsProps) {
    const { formatMessage } = useIntl();

    const isSoundScape = useMemo(
        () => controls === AudioPlayerControls.Soundscape,
        [controls]
    );

    const {
        controls: { togglePlay, isPaused },
    } = useAudio();

    const handleTogglePlay = () => {
        if (onControlClick) {
            // The reverse of the current state, prior to calling togglePlay
            onControlClick(isPaused ? 'play' : 'pause');
        }

        togglePlay();
    };

    return (
        <Slide
            direction="up"
            in={!areControlsHidden}
            mountOnEnter
            unmountOnExit
        >
            <Box sx={sx}>
                <Box
                    sx={sxAppend(
                        styleAudioControlsContainer,
                        isFloating && styleFloatingContainer,
                        controlsSx
                    )}
                >
                    <Box sx={styleAudioControlsMain}>
                        <Box sx={styleAudioControlsLeft}>
                            {image}
                            <IconButton
                                size="legacyMedium"
                                trackButton={
                                    isPaused
                                        ? 'AudioPlayerPlay'
                                        : 'AudioPlayerPause'
                                }
                                onClick={handleTogglePlay}
                                aria-label={
                                    isPaused
                                        ? formatMessage(
                                              playerMessages['player.play']
                                          )
                                        : formatMessage(
                                              playerMessages['player.pause']
                                          )
                                }
                            >
                                {isPaused ? (
                                    <PlayCtaFilled color="primary" />
                                ) : (
                                    <PauseCtaFilled color="primary" />
                                )}
                            </IconButton>
                            {(title || subtitle) && (
                                <Box>
                                    <Typography
                                        variant="body1Demi"
                                        component="p"
                                        color="text.body"
                                        sx={styleMaxLines(1)}
                                    >
                                        {title}
                                    </Typography>
                                    <Typography
                                        variant="subtitle2"
                                        component="p"
                                        color="text.sub"
                                        sx={styleMaxLines(1)}
                                    >
                                        {subtitle}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        <IconButton
                            size="legacyMedium"
                            trackButton={EnumButtonName.Stop}
                            onClick={onStop}
                            aria-label={formatMessage(
                                playerMessages['player.stop']
                            )}
                        >
                            <Cancel color="primary" />
                        </IconButton>
                    </Box>
                    {!isSoundScape && (
                        <Box sx={styleSliderContainer}>
                            <AudioTimeline
                                variant={AudioTimelineVariant.Minimal}
                            />
                        </Box>
                    )}
                </Box>
            </Box>
        </Slide>
    );
}
